import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  Badge,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  VideoCall as VideoCallIcon,
  CallEnd as CallEndIcon,
  Warning as EmergencyIcon,
  LocalHospital as HospitalIcon,
  Phone as PhoneIcon,
  VolumeUp as VolumeIcon,
  Assignment as PrescriptionIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Timer as TimerIcon
} from '@mui/icons-material';

interface PatientInfo {
  id: string;
  name: string;
  age: number;
  phone: string;
  emergencyContact: string;
  medicalHistory: string[];
  currentMedications: string[];
  allergies: string[];
}

interface VoiceSymptomAssessment {
  severity: 'emergency' | 'urgent' | 'moderate' | 'mild';
  confidence: number;
  recommendation: string;
  symptoms: string[];
  transcription: string;
  requiresTelemedicine: boolean;
  emergencyKeywords: string[];
}

interface TelemedicineConsultationProps {
  open: boolean;
  onClose: () => void;
  patientInfo: PatientInfo;
  initialAssessment: VoiceSymptomAssessment;
  onEmergencyActivation: (decision: DoctorDecision) => void;
}

interface DoctorDecision {
  emergencyRequired: boolean;
  urgencyLevel: 'immediate' | 'urgent' | 'routine' | 'none';
  diagnosis: string;
  instructions: string;
  prescription?: string;
  followUp: string;
  activateEmergency: boolean;
  estimatedResponseTime?: string;
  doctorNotes: string;
}

const TelemedicineConsultation: React.FC<TelemedicineConsultationProps> = ({
  open,
  onClose,
  patientInfo,
  initialAssessment,
  onEmergencyActivation
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [callStatus, setCallStatus] = useState<'connecting' | 'connected' | 'ended'>('connecting');
  const [callDuration, setCallDuration] = useState(0);
  const [doctorDecision, setDoctorDecision] = useState<DoctorDecision>({
    emergencyRequired: false,
    urgencyLevel: 'routine',
    diagnosis: '',
    instructions: '',
    followUp: '',
    activateEmergency: false,
    doctorNotes: ''
  });
  
  const [consultationPhase, setConsultationPhase] = useState<'assessment' | 'decision' | 'completed'>('assessment');

  // Simulate video call connection
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setCallStatus('connected');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Call duration timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (callStatus === 'connected') {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [callStatus]);

  const formatCallDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEmergencyActivation = () => {
    const emergencyDecision: DoctorDecision = {
      ...doctorDecision,
      emergencyRequired: true,
      activateEmergency: true,
      urgencyLevel: 'immediate',
      estimatedResponseTime: '8-12 minutos'
    };
    
    onEmergencyActivation(emergencyDecision);
    setConsultationPhase('completed');
  };

  const handleConsultationComplete = () => {
    onEmergencyActivation(doctorDecision);
    setConsultationPhase('completed');
  };

  const endCall = () => {
    setCallStatus('ended');
    onClose();
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'emergency': return <EmergencyIcon color="error" />;
      case 'urgent': return <TimerIcon color="warning" />;
      default: return <CheckIcon color="info" />;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Badge 
              badgeContent={callStatus === 'connected' ? '🟢' : callStatus === 'connecting' ? '🟡' : '🔴'} 
              overlap="circular"
            >
              <Avatar sx={{ bgcolor: 'white', color: 'primary.main' }}>
                {patientInfo.name.charAt(0)}
              </Avatar>
            </Badge>
            <Box>
              <Typography variant="h6">
                Teleconsulta: {patientInfo.name}
              </Typography>
              <Typography variant="body2">
                {callStatus === 'connected' ? `Conectado - ${formatCallDuration(callDuration)}` :
                 callStatus === 'connecting' ? 'Conectando...' : 'Desconectado'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip 
              icon={getSeverityIcon(initialAssessment.severity)}
              label={`IA: ${initialAssessment.severity.toUpperCase()}`}
              color={initialAssessment.severity === 'emergency' ? 'error' : 
                     initialAssessment.severity === 'urgent' ? 'warning' : 'info'}
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {consultationPhase === 'assessment' && (
          <Grid container>
            {/* Video Call Area */}
            <Grid item xs={12} md={8}>
              <Box sx={{ 
                bgcolor: 'black', 
                height: isMobile ? '250px' : '400px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                position: 'relative'
              }}>
                {callStatus === 'connecting' && (
                  <Box sx={{ textAlign: 'center', color: 'white' }}>
                    <VideoCallIcon sx={{ fontSize: 64, mb: 2 }} />
                    <Typography variant="h6">Conectando con {patientInfo.name}...</Typography>
                  </Box>
                )}
                
                {callStatus === 'connected' && (
                  <Box sx={{ textAlign: 'center', color: 'white' }}>
                    <Typography variant="h4">📹</Typography>
                    <Typography variant="h6">Video llamada activa</Typography>
                    <Typography variant="body2">Simulated video feed</Typography>
                  </Box>
                )}

                {/* Call Controls */}
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 16, 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 2
                }}>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<CallEndIcon />}
                    onClick={endCall}
                  >
                    Finalizar
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => setConsultationPhase('decision')}
                    disabled={callStatus !== 'connected'}
                  >
                    Evaluar
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* Patient Information Panel */}
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, height: isMobile ? 'auto' : '400px', overflow: 'auto' }}>
                {/* Initial AI Assessment */}
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      🤖 Evaluación IA Inicial
                    </Typography>
                    <Alert severity={initialAssessment.severity === 'emergency' ? 'error' : 
                                   initialAssessment.severity === 'urgent' ? 'warning' : 'info'}>
                      <Typography variant="body2" fontWeight="bold">
                        {initialAssessment.recommendation}
                      </Typography>
                      <Typography variant="caption">
                        Confianza: {(initialAssessment.confidence * 100).toFixed(0)}%
                      </Typography>
                    </Alert>
                    
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Transcripción:</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                        "{initialAssessment.transcription}"
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Síntomas identificados:</strong>
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {initialAssessment.symptoms.map((symptom, index) => (
                          <Chip key={index} label={symptom} size="small" />
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Patient Medical History */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      📋 Historial Médico
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Edad:</strong> {patientInfo.age} años
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Teléfono:</strong> {patientInfo.phone}
                    </Typography>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Typography variant="body2" gutterBottom>
                      <strong>Antecedentes:</strong>
                    </Typography>
                    <List dense>
                      {patientInfo.medicalHistory.map((condition, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemText primary={`• ${condition}`} />
                        </ListItem>
                      ))}
                    </List>

                    <Typography variant="body2" gutterBottom>
                      <strong>Medicamentos actuales:</strong>
                    </Typography>
                    <List dense>
                      {patientInfo.currentMedications.map((medication, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemText primary={`• ${medication}`} />
                        </ListItem>
                      ))}
                    </List>

                    {patientInfo.allergies.length > 0 && (
                      <>
                        <Typography variant="body2" gutterBottom color="error">
                          <strong>⚠️ Alergias:</strong>
                        </Typography>
                        <List dense>
                          {patientInfo.allergies.map((allergy, index) => (
                            <ListItem key={index} sx={{ py: 0 }}>
                              <ListItemText primary={`• ${allergy}`} />
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        )}

        {consultationPhase === 'decision' && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Decisión Médica - {patientInfo.name}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" sx={{ mb: 3 }}>
                  <FormLabel component="legend">Nivel de Urgencia</FormLabel>
                  <RadioGroup
                    value={doctorDecision.urgencyLevel}
                    onChange={(e) => setDoctorDecision(prev => ({
                      ...prev,
                      urgencyLevel: e.target.value as any
                    }))}
                  >
                    <FormControlLabel 
                      value="immediate" 
                      control={<Radio />} 
                      label="🚨 EMERGENCIA INMEDIATA - Activar protocolo de emergencia" 
                    />
                    <FormControlLabel 
                      value="urgent" 
                      control={<Radio />} 
                      label="⚠️ URGENTE - Requiere atención en 1-2 horas" 
                    />
                    <FormControlLabel 
                      value="routine" 
                      control={<Radio />} 
                      label="📅 RUTINARIO - Control en 24-48 horas" 
                    />
                    <FormControlLabel 
                      value="none" 
                      control={<Radio />} 
                      label="✅ NO URGENTE - Seguimiento opcional" 
                    />
                  </RadioGroup>
                </FormControl>

                <TextField
                  fullWidth
                  label="Diagnóstico Preliminar"
                  multiline
                  rows={2}
                  value={doctorDecision.diagnosis}
                  onChange={(e) => setDoctorDecision(prev => ({
                    ...prev,
                    diagnosis: e.target.value
                  }))}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Instrucciones para el Paciente"
                  multiline
                  rows={3}
                  value={doctorDecision.instructions}
                  onChange={(e) => setDoctorDecision(prev => ({
                    ...prev,
                    instructions: e.target.value
                  }))}
                  sx={{ mb: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Notas del Médico"
                  multiline
                  rows={4}
                  value={doctorDecision.doctorNotes}
                  onChange={(e) => setDoctorDecision(prev => ({
                    ...prev,
                    doctorNotes: e.target.value
                  }))}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Seguimiento Requerido"
                  value={doctorDecision.followUp}
                  onChange={(e) => setDoctorDecision(prev => ({
                    ...prev,
                    followUp: e.target.value
                  }))}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Prescripción (Opcional)"
                  multiline
                  rows={2}
                  value={doctorDecision.prescription || ''}
                  onChange={(e) => setDoctorDecision(prev => ({
                    ...prev,
                    prescription: e.target.value
                  }))}
                />
              </Grid>
            </Grid>

            {doctorDecision.urgencyLevel === 'immediate' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  ⚠️ ACTIVACIÓN DE EMERGENCIA
                </Typography>
                <Typography variant="body2">
                  Al confirmar, se activará inmediatamente el protocolo de emergencia:
                  ambulancia, notificación a familia, y preparación hospitalaria.
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {consultationPhase === 'completed' && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Consulta Completada
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Las instrucciones han sido enviadas al paciente y familia.
            </Typography>
            {doctorDecision.activateEmergency && (
              <Alert severity="success">
                <Typography variant="body1" fontWeight="bold">
                  🚨 Protocolo de emergencia activado exitosamente
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        {consultationPhase === 'decision' && (
          <>
            <Button 
              onClick={() => setConsultationPhase('assessment')}
              variant="outlined"
            >
              Volver a Evaluación
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {doctorDecision.urgencyLevel === 'immediate' ? (
                <Button
                  onClick={handleEmergencyActivation}
                  variant="contained"
                  color="error"
                  startIcon={<EmergencyIcon />}
                  size="large"
                >
                  ACTIVAR EMERGENCIA
                </Button>
              ) : (
                <Button
                  onClick={handleConsultationComplete}
                  variant="contained"
                  color="primary"
                  startIcon={<CheckIcon />}
                >
                  Completar Consulta
                </Button>
              )}
            </Box>
          </>
        )}
        
        {(consultationPhase === 'assessment' || consultationPhase === 'completed') && (
          <Button onClick={onClose} variant="contained">
            {consultationPhase === 'completed' ? 'Cerrar' : 'Cancelar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TelemedicineConsultation;